import type { ScenarioConfig } from '../../engine/plant/types';

const H = 3600;

export const scenarios: ScenarioConfig[] = [
  {
    id: 'steady',
    name: 'Steady shift',
    category: 'sandbox',
    brief:
      'Design conditions on a light crude: 500 m³/h charged, coil outlet 350 °C, tower at ' +
      '1.8 bara, everything on spec. Use it to learn the unit and to see how long the tower ' +
      'takes to answer a question.',
    durationS: 8 * H,
    events: [],
    objectives: [
      { id: 'nap', text: 'Keep the naphtha endpoint below 438 K', kind: 'maintain', condition: { op: '<', tag: 'AT-304.PV', value: 438 }, toleranceS: 120, weight: 3 },
      { id: 'dsl', text: 'Keep the diesel 95 % point below 623 K', kind: 'maintain', condition: { op: '<', tag: 'AT-306.PV', value: 623 }, toleranceS: 120, weight: 3 },
      { id: 'skin', text: 'Keep the tube skin below 723 K', kind: 'maintain', condition: { op: '<', tag: 'TT-202.PV', value: 723 }, toleranceS: 30, weight: 3 },
    ],
    stars: [55, 78, 93],
    tip: 'The column answers slowly: four minutes at the top, seven on the side draws, fifteen at the bottom. Make one change and wait.',
  },

  {
    id: 'crude-switch',
    name: 'Crude switch',
    category: 'campaign',
    brief:
      'Tankage is switching to a heavier assay over the next forty-five minutes. Fourteen per ' +
      'cent more residue means less of everything valuable, a colder flash for the same coil ' +
      'outlet, and cut points that will drift if you leave them alone.',
    durationS: 6 * H,
    events: [
      { id: 'switch', at: 25 * 60, effects: [{ do: 'fault', id: 'HEAVY-CRUDE', on: true }], announce: 'Tank switch under way — heavier assay on line.', hint: 'A heavier crude flashes less at the same coil outlet. Expect the bottoms level to climb and the side draws to go light unless you follow them.' },
    ],
    objectives: [
      { id: 'nap', text: 'Keep the naphtha endpoint below 438 K', kind: 'maintain', condition: { op: '<', tag: 'AT-304.PV', value: 438 }, toleranceS: 900, weight: 3 },
      { id: 'dsl', text: 'Keep the diesel 95 % point below 623 K', kind: 'maintain', condition: { op: '<', tag: 'AT-306.PV', value: 623 }, toleranceS: 900, weight: 3 },
      { id: 'level', text: 'Keep the bottoms level between 20 % and 82 %', kind: 'maintain', condition: { op: 'between', tag: 'LT-307.PV', lo: 20, hi: 82 }, toleranceS: 300, weight: 3 },
      { id: 'rate', text: 'Hold at least 420 m³/h of charge', kind: 'maintain', condition: { op: '>', tag: 'FT-101.PV', value: 420 }, toleranceS: 600, weight: 2 },
    ],
    stars: [50, 74, 90],
    tip: 'The material balance is the first thing to fail on a crude switch. Watch the bottoms level before you watch the analysers.',
  },

  {
    id: 'heater-fouling',
    name: 'Fouling across a shift',
    category: 'campaign',
    brief:
      'The heater coil is coking. Over the shift the tube skin will creep up for the same ' +
      'absorbed duty. The trip is at 480 °C on the skin, and it takes the unit down with it.',
    durationS: 8 * H,
    events: [
      { id: 'foul', at: 15 * 60, effects: [{ do: 'fault', id: 'HEATER-FOUL', on: true }] },
      { id: 'foul2', at: 2 * H, effects: [{ do: 'fault', id: 'HX103-FOUL', on: true }], hint: 'The residue preheat exchanger is fouling too — less preheat means the heater has to work harder for the same coil outlet.' },
    ],
    objectives: [
      { id: 'skin', text: 'Keep the tube skin below 723 K', kind: 'maintain', condition: { op: '<', tag: 'TT-202.PV', value: 723 }, toleranceS: 300, weight: 4 },
      { id: 'notrip', text: 'No heater trip', kind: 'avoid', condition: { op: 'tripped', id: 'I-201' }, weight: 4 },
      { id: 'rate', text: 'Keep at least 400 m³/h on charge', kind: 'maintain', condition: { op: '>', tag: 'FT-101.PV', value: 400 }, toleranceS: 900, weight: 2 },
    ],
    stars: [50, 75, 92],
    tip: 'Skin temperature falls with charge rate at fixed duty. Trading a little throughput for coil life is usually the right call.',
  },

  {
    id: 'condenser-loss',
    name: 'Losing the condenser',
    category: 'campaign',
    brief:
      'The condenser cooling-water valve is about to jam almost shut. Without condensing duty ' +
      'the tower pressure has nowhere to go, and every cut point goes with it.',
    durationS: 4 * H,
    events: [
      { id: 'loss', at: 20 * 60, effects: [{ do: 'fault', id: 'COND-LOSS', on: true }], announce: 'Condenser cooling-water flow falling.', hint: 'Pressure is made by vapour the condenser cannot take away. Cut the vapour: firing first, then charge.' },
    ],
    objectives: [
      { id: 'notrip', text: 'Keep the column below 2.6 bara — no pressure trip', kind: 'avoid', condition: { op: 'tripped', id: 'I-301' }, weight: 5 },
      { id: 'press', text: 'Keep the column below 2.4 bara', kind: 'maintain', condition: { op: '<', tag: 'PT-301.PV', value: 2.4 }, toleranceS: 300, weight: 3 },
      { id: 'drum', text: 'Keep the reflux drum above 22 %', kind: 'maintain', condition: { op: '>', tag: 'LT-302.PV', value: 22 }, toleranceS: 180, weight: 3 },
    ],
    stars: [45, 72, 90],
    tip: 'Firing is the fastest lever on tower pressure. Charge rate is slower but it stays reduced.',
  },

  {
    id: 'reflux-trip',
    name: 'Reflux pump trip',
    category: 'campaign',
    brief:
      'The reflux pump is going to trip. The top of a tower without reflux loses its ' +
      'separation in minutes, and the naphtha will go off spec long before the temperature ' +
      'looks alarming.',
    durationS: 3 * H,
    events: [
      { id: 'trip', at: 20 * 60, effects: [{ do: 'fault', id: 'P303-TRIP', on: true }], announce: 'Reflux pump has tripped.', hint: 'With no reflux the condenser still condenses, so the drum will fill. Cut the firing to reduce the overhead load while you get the pump back.' },
      { id: 'restored', at: 55 * 60, effects: [{ do: 'fault', id: 'P303-TRIP', on: false }], announce: 'Reflux pump restarted by the field operator.' },
    ],
    objectives: [
      { id: 'drum', text: 'Do not overfill the reflux drum', kind: 'maintain', condition: { op: '<', tag: 'LT-302.PV', value: 90 }, toleranceS: 120, weight: 3 },
      { id: 'press', text: 'Keep the column below 2.4 bara', kind: 'maintain', condition: { op: '<', tag: 'PT-301.PV', value: 2.4 }, toleranceS: 180, weight: 3 },
      { id: 'recover', text: 'Get the naphtha endpoint back on spec by the end of the shift', kind: 'achieve', condition: { op: 'and', terms: [{ op: '<', tag: 'AT-304.PV', value: 438 }, { op: 'simTimeAfter', seconds: 2 * H }] }, weight: 4 },
    ],
    stars: [45, 72, 90],
    tip: 'Reflux is what makes the separation. Without it, cutting the vapour load is the only way to protect the products.',
  },

  {
    id: 'fuel-swing',
    name: 'Fuel-gas pressure swing',
    category: 'campaign',
    brief:
      'Another unit is about to take gas off the header. The fuel-gas pressure will drop and ' +
      'the firing with it — unless the inner loop of the heater cascade does its job.',
    durationS: 3 * H,
    events: [
      { id: 'swing', at: 20 * 60, effects: [{ do: 'fault', id: 'FG-SWING', on: true }], hint: 'FIC-202 in CAS rejects the pressure swing before it reaches the coil. In MAN, it lands squarely on your temperature.' },
      { id: 'back', at: 70 * 60, effects: [{ do: 'fault', id: 'FG-SWING', on: false }], announce: 'Fuel-gas header pressure recovering.' },
    ],
    objectives: [
      { id: 'coil', text: 'Hold the coil outlet within 8 K of setpoint', kind: 'maintain', condition: { op: 'between', tag: 'TT-201.PV', lo: 615, hi: 631 }, toleranceS: 300, weight: 4 },
      { id: 'cas', text: 'Leave the fuel flow controller in cascade', kind: 'maintain', condition: { op: 'mode', loop: 'FIC-202', is: 'CAS' }, toleranceS: 60, weight: 3 },
      { id: 'spec', text: 'Keep the diesel on spec', kind: 'maintain', condition: { op: '<', tag: 'AT-306.PV', value: 623 }, toleranceS: 300, weight: 2 },
    ],
    stars: [50, 76, 92],
    tip: 'This is the textbook case for cascade control: a fast inner loop on the thing that moves, so the slow outer loop never sees it.',
  },

  {
    id: 'flooding',
    name: 'Flooding recovery',
    category: 'campaign',
    brief:
      'The night shift pushed the charge rate and the firing to make up a shortfall. The ' +
      'tower is close to flood: ΔP is already at the knee. Get it back before the trays fill ' +
      'and every product goes off at once.',
    durationS: 4 * H,
    events: [
      { id: 'push', at: 60, effects: [{ do: 'setSp', loop: 'FIC-101', value: 640 }, { do: 'setSp', loop: 'TIC-201', value: 636 }], announce: 'Handover: charge and firing were both pushed overnight.', hint: 'ΔP rises with the square of the vapour load, and steeply past the knee. Reduce the firing first — it is the fastest way to take vapour out of the tower.' },
    ],
    objectives: [
      { id: 'noflood', text: 'Do not flood the column', kind: 'avoid', condition: { op: '>', tag: 'C-301.FLOOD', value: 0.5 }, weight: 5 },
      { id: 'dp', text: 'Bring the column ΔP below 0.5 bar', kind: 'achieve', condition: { op: '<', tag: 'PDT-301.PV', value: 0.5 }, weight: 3 },
      { id: 'spec', text: 'Get both products back on spec', kind: 'achieve', condition: { op: 'and', terms: [{ op: '<', tag: 'AT-304.PV', value: 438 }, { op: '<', tag: 'AT-306.PV', value: 623 }] }, weight: 3 },
    ],
    stars: [45, 72, 90],
    tip: 'Raising the tower pressure shrinks the vapour volume and buys you room immediately — at the cost of every cut point moving up.',
  },

  {
    id: 'cold-cw',
    name: 'A hot afternoon',
    category: 'campaign',
    brief:
      'Cooling-water supply is climbing ten degrees over the next hour and a half. The ' +
      'condenser loses duty with it, and the tower pressure follows. Hold the unit on spec ' +
      'without letting the pressure run away.',
    durationS: 5 * H,
    events: [
      { id: 'warm', at: 20 * 60, effects: [{ do: 'fault', id: 'CW-WARM', on: true }] },
    ],
    objectives: [
      { id: 'press', text: 'Keep the column below 2.15 bara', kind: 'maintain', condition: { op: '<', tag: 'PT-301.PV', value: 2.15 }, toleranceS: 600, weight: 4 },
      { id: 'nap', text: 'Keep the naphtha endpoint below 438 K', kind: 'maintain', condition: { op: '<', tag: 'AT-304.PV', value: 438 }, toleranceS: 600, weight: 3 },
      { id: 'rate', text: 'Keep at least 430 m³/h on charge', kind: 'maintain', condition: { op: '>', tag: 'FT-101.PV', value: 430 }, toleranceS: 900, weight: 2 },
    ],
    stars: [50, 75, 91],
    tip: 'When the condenser is the constraint, the only real lever is how much vapour you send it.',
  },
];

/** Tuning challenge on the heater coil-outlet cascade. */
export const tuningChallenges: ScenarioConfig[] = [
  {
    id: 'tune-tic201',
    name: 'Tuning challenge — heater coil outlet',
    category: 'tuning',
    challengeLoop: 'TIC-201',
    challengeTarget: 1100,
    brief:
      'The most important temperature on the unit, with a fuel-gas header that will not hold ' +
      'still. Two setpoint changes and a pressure swing over an hour, scored on the integrated ' +
      'absolute error of TIC-201. The tube skin still has to stay below 723 K.',
    durationS: 60 * 60,
    events: [
      { id: 'sp1', at: 6 * 60, effects: [{ do: 'setSp', loop: 'TIC-201', value: 631 }] },
      { id: 'fg', at: 20 * 60, effects: [{ do: 'fault', id: 'FG-SWING', on: true }], announce: 'Fuel-gas header pressure falling.' },
      { id: 'fgoff', at: 34 * 60, effects: [{ do: 'fault', id: 'FG-SWING', on: false }] },
      { id: 'sp2', at: 42 * 60, effects: [{ do: 'setSp', loop: 'TIC-201', value: 617 }] },
    ],
    objectives: [
      { id: 'iae', text: 'Keep the integrated absolute error below 1100 %·s', kind: 'maintain', condition: { op: '<', tag: 'TIC-201.IAE', value: 1100 }, toleranceS: 0, weight: 5 },
      { id: 'skin', text: 'Keep the tube skin below 723 K', kind: 'maintain', condition: { op: '<', tag: 'TT-202.PV', value: 723 }, toleranceS: 0, weight: 4 },
      { id: 'notrip', text: 'No heater trip', kind: 'avoid', condition: { op: 'tripped', id: 'I-201' }, weight: 3 },
    ],
    stars: [50, 74, 92],
    tip: 'Most of the disturbance rejection here belongs to the inner fuel-flow loop. Tune that first and the outer loop has an easy job.',
  },
];
